import React, {
	useCallback,
	type ComponentPropsWithoutRef,
	type ComponentType,
	type ReactNode,
} from 'react';
import { useReportTerminalError } from '@atlaskit/react-ufo/set-terminal-error';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import ErrorBoundary from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import { getErrorHash } from '@jira/platform__error-reporting/src/getErrorHash.tsx';
import { ReportErrors } from '@jira/platform__error-reporting/src/ReportErrors.tsx';
import type { ErrorFlagManager } from './ErrorFlagManager.tsx';

type ErrorFlagRendererProps = {
	id: string;
	packageName?: string;
	teamName?: string;
	error: Error | null;
};

// dupliacted from jira-error-boundary
type ExtraEventData = {
	[attributeKey: string]: string | boolean | number;
};

// dupliacted from jira-error-boundary
type ErrorBoundaryFlagProps = {
	children: ReactNode;
	extraEventData?: ExtraEventData;
	withExperienceTracker?: boolean;
	id?: string;
	packageName?: string;
	teamName?: string;
};

const ErrorFlagRendererLazy: ComponentType<ComponentPropsWithoutRef<typeof ErrorFlagManager>> =
	// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
	lazy(() =>
		import(/* webpackChunkName: "async-error-flag-renderer" */ './ErrorFlagManager.tsx').then(
			(m) => m.ErrorFlagManager,
		),
	);

/**
 * @deprecated Do not use directly, instead use `JSErrorBoundary` in
 * `@atlassian/jira-error-boundaries`, which will log appropriate errors to
 * Sentry/SFX/Splunk. See https://hello.atlassian.net/wiki/spaces/JFP/pages/1890192830/Jira+Playbook+Uncaught+error+handling
 */
export const ErrorFlagRenderer = ({
	id,
	error,
	packageName,
	teamName,
}: ErrorFlagRendererProps): React.JSX.Element => {
	useReportTerminalError(error, {
		teamName,
		packageName,
		errorBoundaryId: id,
		fallbackType: 'flag',
		errorHash: error ? getErrorHash(error) : undefined,
	});

	return (
		<ErrorBoundary id={id} packageName={packageName ?? 'jiraErrorBoundary'} teamName={teamName}>
			<ReportErrors id={id} packageName={packageName ?? 'jiraErrorBoundary'} teamName={teamName}>
				<Placeholder name="async-error-flag-renderer" fallback={null}>
					<ErrorFlagRendererLazy id={id} error={error} />
				</Placeholder>
			</ReportErrors>
		</ErrorBoundary>
	);
};

const DEFAULT_EXTRA_EVENT_DATA = {};

/**
 * An error boundary that will render an atlaskit flag when a render exception
 * is thrown.
 *
 * @deprecated Do not use directly, instead use `JSErrorBoundary` in
 * `@atlassian/jira-error-boundaries`, which will log appropriate errors to
 * Sentry/SFX/Splunk. See https://hello.atlassian.net/wiki/spaces/JFP/pages/1890192830/Jira+Playbook+Uncaught+error+handling
 */
export const ErrorBoundaryFlag = ({
	children,
	extraEventData = DEFAULT_EXTRA_EVENT_DATA,
	id = undefined,
	packageName,
	teamName,
	withExperienceTracker,
}: ErrorBoundaryFlagProps): React.JSX.Element => {
	const renderErrorFlag = useCallback(
		({ error }: { error: Error }) => (
			<ErrorFlagRenderer
				id="error-boundary"
				error={error}
				packageName={packageName}
				teamName={teamName}
			/>
		),
		[packageName, teamName],
	);

	return (
		<ErrorBoundary
			extraEventData={extraEventData}
			id={id}
			packageName={packageName}
			teamName={teamName}
			withExperienceTracker={withExperienceTracker}
			render={renderErrorFlag}
		>
			{children}
		</ErrorBoundary>
	);
};
